import styled, { css, keyframes } from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueMui,
  greenMui,
  greyMui,
  lightBlueMui,
  MOBILE_BREAKPOINT,
  orangeMui,
  purpleMui,
  redMui,
  rydeColors,
  ScreenDepth,
} from "@ryde/theme";

const subtleLoaderShimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

export const RichViewLogsDrawerWrapperStyled = css<{ $isRtl: boolean }>`
  padding: 24px;

  .log__filters-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 20px;

    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      gap: 12px;
      margin-bottom: 16px;
    }
  }

  .log__collapsible-filter-section {
    width: 100%;
    box-sizing: border-box;
    align-self: stretch;
    background-color: ${greyMui._50};
    border-radius: 12px;
    border: 1px solid ${greyMui._200};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    transition: box-shadow 0.2s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    }

    &.collapsed {
      background-color: #fff;
    }

    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      border-radius: 8px;
    }
  }

  .log__collapsible-filter-section__toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    margin: 0;
    padding: 10px 12px;
    border: 0;
    background: transparent;
    cursor: pointer;
    text-align: start;
    color: ${greyMui._800};
    font: inherit;

    &:hover {
      background-color: ${greyMui._100};
    }

    &:focus-visible {
      outline: 2px solid ${blueMui._400};
      outline-offset: -2px;
    }
  }

  .log__collapsible-filter-section__emoji {
    flex-shrink: 0;
    font-size: 16px;
    line-height: 1;
  }

  .log__collapsible-filter-section__title {
    flex: 1;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    color: ${greyMui._800};
  }

  .log__collapsible-filter-section__arrow {
    flex-shrink: 0;
    color: ${greyMui._600};
    transition: transform 0.2s ease;

    &.expanded {
      transform: rotate(180deg);
    }
  }

  .log__collapsible-filter-section__content {
    padding: 0 16px 16px;
    transition:
      max-height 0.3s ease,
      opacity 0.2s ease,
      padding 0.2s ease;

    &.collapsed {
      max-height: 0;
      padding-top: 0;
      padding-bottom: 0;
      opacity: 0;
      overflow: hidden;
      pointer-events: none;
    }

    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      padding: 0 12px 12px;
    }
  }

  .log__filters-panel.log__collapsible-filter-section.collapsed
    .log__collapsible-filter-section__toggle {
    background-color: ${greyMui._50};
  }

  .log__filters-panel {
    padding: 0;

    .log__filters {
      padding-top: 4px;
    }
  }

  .log__filters-panel.log__collapsible-filter-section.collapsed {
    display: block;
    min-height: 0;
    padding: 0;
  }

  .log__top-loading {
    position: absolute;
    top: -8px;
    inset-inline-start: 12px;
    inset-inline-end: 12px;
    height: 4px;
    border-radius: 999px;
    background: linear-gradient(
      90deg,
      ${greyMui._100},
      ${greyMui._200},
      ${greyMui._100}
    );
    background-size: 200% 100%;
    animation: ${subtleLoaderShimmer} 2.6s ease-in-out infinite;
    opacity: 0.9;
  }

  .log__stats-panel {
    background-color: ${greyMui._50};
    border-radius: 12px;
    padding: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid ${greyMui._200};
    transition: box-shadow 0.2s ease;
    position: relative;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    }

    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      padding: 10px;
      border-radius: 8px;
    }
  }

  .log__stats-tabs {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .log__stats-tabs-header {
    display: flex;
    border-bottom: 1px solid ${greyMui._300};
    margin-bottom: 12px;
  }

  .log__stats-tab {
    background: none;
    border: none;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    color: ${greyMui._600};
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.2s ease;
    position: relative;

    &:hover {
      color: ${blueMui._700};
      background-color: ${greyMui._100};
    }

    &.active {
      color: ${blueMui._700};
      border-bottom-color: ${blueMui._500};
      background-color: ${blueMui._50};
    }

    &:first-child {
      border-start-start-radius: 6px;
    }

    &:last-child {
      border-start-end-radius: 6px;
    }
  }

  .log__stats-tabs-content {
    min-height: 60px;
  }

  .log__stats-tab-panel {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .log__metrics-section {
    border: 1px solid ${greyMui._300};
    border-radius: 8px;
    padding: 12px;
    background-color: white;
  }

  .log__metrics-section-title {
    font-size: 13px;
    font-weight: 600;
    color: ${greyMui._800};
    margin-bottom: 8px;
    padding-bottom: 4px;
    border-bottom: 1px solid ${greyMui._200};
  }

  .log__metrics-booking-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .log__metrics-map-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .log__metrics-map-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    font-size: 12px;
    line-height: 1.2;
    padding: 6px 8px;
    background-color: ${greyMui._50};
    border-radius: 4px;
    border: 1px solid ${greyMui._200};
    min-height: 28px;
    max-width: 100%;
    overflow: hidden;
  }

  .log__metrics-map-label {
    color: ${greyMui._700};
    font-weight: 500;
    flex: 1;
    margin-inline-end: 8px;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .log__metrics-map-value {
    color: ${blueMui._700};
    font-weight: 600;
    min-width: 40px;
    max-width: 80px;
    text-align: center;
    padding: 1px 4px;
    background-color: ${lightBlueMui._100};
    border-radius: 12px;
    border: 1px solid ${lightBlueMui._300};
    font-size: 11px;
    flex-shrink: 0;
    word-wrap: break-word;
    white-space: normal;
    line-height: 1.2;
    min-height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-inline-start: auto;
  }

  .log__metrics-map-label-booking {
    color: ${greenMui._700};
  }

  .log__metrics-map-label-hub-created {
    color: ${orangeMui._800};
  }

  .log__metrics-map-label-hub-routed {
    color: ${blueMui._800};
  }

  .log__metrics-map-value-booking {
    color: ${blueMui._700};
    background-color: ${lightBlueMui._100};
    border-color: ${lightBlueMui._300};
    max-width: 80px;
    word-wrap: break-word;
    white-space: normal;
    line-height: 1.2;
    min-height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-inline-start: auto;
  }

  .log__metrics-map-label-error {
    color: ${redMui._700};
  }

  .log__metrics-map-value-error {
    color: ${blueMui._700};
    background-color: ${lightBlueMui._100};
    border-color: ${lightBlueMui._300};
    max-width: 80px;
    word-wrap: break-word;
    white-space: normal;
    line-height: 1.2;
    min-height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-inline-start: auto;
  }

  .log__metrics-map-label-optimized {
    color: ${blueMui._700};
  }

  .log__metrics-map-value-clickable {
    cursor: pointer;
    text-decoration: underline;
  }

  .log__metrics-map-value-clickable-green {
    cursor: pointer;
    text-decoration: underline;
    font-weight: 600;
    min-width: 40px;
    max-width: 80px;
    text-align: center;
    padding: 1px 4px;
    border-radius: 12px;
    border: 1px solid ${greenMui._300};
    font-size: 11px;
    flex-shrink: 0;
    word-wrap: break-word;
    white-space: normal;
    line-height: 1.2;
    min-height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-inline-start: auto;
    background-color: ${greenMui._100};
    color: ${greenMui._700};
  }

  .log__metrics-map-value-clickable-orange {
    cursor: pointer;
    text-decoration: underline;
    font-weight: 600;
    min-width: 40px;
    max-width: 80px;
    text-align: center;
    padding: 1px 4px;
    border-radius: 12px;
    border: 1px solid ${orangeMui._300};
    font-size: 11px;
    flex-shrink: 0;
    word-wrap: break-word;
    white-space: normal;
    line-height: 1.2;
    min-height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-inline-start: auto;
    background-color: ${orangeMui._100};
    color: ${orangeMui._700};
  }

  .log__metrics-map-value-clickable-red {
    cursor: pointer;
    text-decoration: underline;
    font-weight: 600;
    min-width: 40px;
    max-width: 80px;
    text-align: center;
    padding: 1px 4px;
    border-radius: 12px;
    border: 1px solid ${redMui._300};
    font-size: 11px;
    flex-shrink: 0;
    word-wrap: break-word;
    white-space: normal;
    line-height: 1.2;
    min-height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-inline-start: auto;
    background-color: ${redMui._100};
    color: ${redMui._700};
  }

  .log__metrics-map-value-optimized {
    color: ${blueMui._700};
    background-color: ${lightBlueMui._100};
    border-color: ${lightBlueMui._300};
    max-width: 80px;
    word-wrap: break-word;
    white-space: normal;
    line-height: 1.2;
    min-height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-inline-start: auto;
  }

  .log__metrics-section-filters .log__metrics-filters-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .log__metrics-filter-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 12px;
    line-height: 1.2;
    padding: 6px 8px;
    background-color: ${greyMui._50};
    border-radius: 4px;
    border: 1px solid ${greyMui._200};
    min-height: 28px;
  }

  .log__metrics-filter-label {
    color: ${greyMui._700};
    font-weight: 500;
    flex-shrink: 0;
    max-width: 140px;
  }

  .log__metrics-filter-value-bubble {
    color: ${greyMui._800};
    font-weight: 500;
    padding: 2px 8px;
    background-color: ${greyMui._200};
    border-radius: 12px;
    border: 1px solid ${greyMui._300};
    font-size: 11px;
    line-height: 1.3;
    min-height: 20px;
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    margin-inline-start: auto;
    max-width: 100%;
    word-break: break-word;
  }

  .log__metrics-filter-ride-type-id-link {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: ${blueMui._700};
    text-decoration: underline;
    cursor: pointer;
  }

  .log__metrics-filter-ride-type-id-link:hover {
    color: ${blueMui._900};
  }

  .log__system-param-value {
    font-weight: 600;
    min-width: 60px;
    max-width: 120px;
    text-align: center;
    padding: 2px 6px;
    border-radius: 12px;
    border: 1px solid;
    font-size: 11px;
    flex-shrink: 0;
    line-height: 1.2;
    min-height: 16px;
    display: inline-block;
    margin-inline-start: auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .log__system-param-value-green {
    background-color: ${greenMui._100};
    color: ${greenMui._700};
    border-color: ${greenMui._300};
  }

  .log__system-param-value-blue {
    background-color: ${lightBlueMui._100};
    color: ${blueMui._700};
    border-color: ${lightBlueMui._300};
  }

  .log__metrics-category-group {
    margin-bottom: 16px;
  }

  .log__metrics-category-title {
    font-size: 12px;
    font-weight: 600;
    color: ${greyMui._700};
    margin-bottom: 8px;
    padding: 4px 8px;
    background-color: ${greyMui._100};
    border-radius: 6px;
    border-inline-start: 3px solid ${blueMui._500};
  }

  .log__filters {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 15px;
    row-gap: 15px;
    .log__filters--multiselect-container {
      grid-column: span 2;
    }

    .log__filters--search-row {
      grid-column: span 2;
      display: flex;
      gap: 15px;
      align-items: flex-start;
      min-width: 0;
    }

    .log__filters--search-field {
      flex: 1;
      min-width: 0;
    }

    .log__filters--comma-mode-field {
      flex: 0 0 120px;
      min-width: 120px;
    }

    .log__filters--group-field {
      flex: 0 0 88px;
      min-width: 0;
      max-width: 88px;

      .MuiFormControl-root,
      [data-e2e="number-template"] {
        width: 100%;
      }
    }

    .log__filters--sort-step-row {
      grid-column: span 2;
      display: flex;
      gap: 12px;
      align-items: flex-start;
      min-width: 0;
    }

    .log__filters-sort-dropdown--sort {
      flex: 0 0 40%;
      max-width: 248px;
      min-width: 0;
    }

    .log__filters-sort-dropdown--step {
      flex: 1;
      min-width: 0;
    }

    .log__filters-sort-dropdown--step .log__filters-sort-value {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      grid-template-columns: 1fr;
      .log__filters--multiselect-container,
      .log__filters--search-row,
      .log__filters--sort-step-row {
        grid-column: span 1;
      }

      .log__filters--search-row,
      .log__filters--sort-step-row {
        flex-direction: column;
      }

      .log__filters--comma-mode-field,
      .log__filters--group-field,
      .log__filters-sort-dropdown--sort,
      .log__filters-sort-dropdown--step {
        flex: 1 1 auto;
        max-width: none;
        width: 100%;
      }
    }
  }

  .log__filters--reset-button-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .log__filters--reset-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .log__filters-refresh-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    margin: 0;
    border: 1px solid ${greyMui._300};
    border-radius: 6px;
    background: none;
    cursor: pointer;
    color: ${greyMui._600};
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background-color: ${greyMui._100};
      border-color: ${greyMui._400};
      color: ${greyMui._800};
    }

    &:focus {
      outline: none;
      border-color: ${blueMui._400};
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  .log__filters--debug-checkbox-container {
    .MuiFormControlLabel-root {
      margin-inline-start: 0;
      margin-inline-end: 0;

      .MuiFormControlLabel-label {
        font-size: 12px;
        color: ${greyMui._600};
        margin-inline-start: 4px;
        font-weight: 500;
      }

      .MuiCheckbox-root {
        padding: 2px;

        .MuiSvgIcon-root {
          font-size: 18px;
          color: ${greyMui._500};
        }

        &.Mui-checked .MuiSvgIcon-root {
          color: ${greyMui._600};
        }
      }
    }
  }

  .log__item {
    padding: 6px;
    margin-bottom: 8px;
    border-radius: 6px;
    border-inline-start: 4px solid ${blueMui._500};
    color: ${greyMui._900};
    background-color: ${greyMui._50};
    word-wrap: break-word;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition:
      transform 0.1s ease,
      box-shadow 0.1s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    }

    .log__item--time {
      font-size: 11px;
      color: ${blueMui._700};
      padding-inline-end: 8px;
      font-weight: 500;
    }

    .log__item--group {
      font-size: 11px;
      color: ${greyMui._600};
      padding-inline: 8px;
      font-weight: 500;
    }

    .log__item--step {
      font-size: 11px;
      color: ${greyMui._400};
      padding-inline: 8px;
      font-weight: 500;
    }

    .log__item--method-line {
      display: block;
      margin-top: 4px;
      margin-bottom: 4px;
    }

    .log__item--method-label {
      font-size: 10px;
      color: ${greyMui._700};
      font-weight: 600;
      margin-inline-end: 6px;
    }

    .log__item--method-value {
      font-size: 10px;
      color: ${greyMui._700};
      font-weight: 600;

      .log-highlight {
        background-color: ${rydeColors.yellow};
        border-radius: 2px;
        padding: 0 2px;
        font-weight: 500;
      }
    }

    .log__item--ai-hint-line {
      display: block;
      margin-top: 4px;
      margin-bottom: 4px;
    }

    .log__item--ai-hint-label {
      font-size: 10px;
      color: ${greyMui._600};
      font-weight: 400;
      margin-inline-end: 6px;
    }

    .log__item--ai-hint-value {
      font-size: 10px;
      color: ${greyMui._600};
      font-weight: 400;

      .log-highlight {
        background-color: ${rydeColors.yellow};
        border-radius: 2px;
        padding: 0 2px;
        font-weight: 400;
      }
    }

    .log__item--level {
      font-size: 11px;
      font-weight: 600;
      padding-inline: 8px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      width: 70px;
      display: inline-block;
      &_critical {
        color: ${redMui._700};
      }
      &_error {
        color: ${redMui._500};
      }
      &_warn {
        color: ${orangeMui._700};
      }
      &_info {
        color: ${blueMui._700};
      }
      &_debug {
        color: ${greyMui._600};
      }
      &_details {
        color: ${purpleMui._700};
      }
    }
    &.log__item--level_critical {
      border-inline-start-color: ${redMui._700};
    }

    &.log__item--level_error {
      border-inline-start-color: ${redMui._300};
    }

    &.log__item--level_warn {
      border-inline-start-color: ${orangeMui._500};
    }

    &.log__item--level_info {
      border-inline-start-color: ${blueMui._500};
    }

    &.log__item--level_debug {
      border-inline-start-color: ${greyMui._400};
    }

    &.log__item--level_details {
      border-inline-start-color: ${purpleMui._500};
    }

    .log__item--message a {
      color: ${lightBlueMui._700};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
        cursor: pointer;
      }
    }

    .log__item--message {
      margin: 6px 0 0;
      font-size: 13px;
      line-height: 1.4;
      color: ${greyMui._800};
    }

    .log__item--translated-info {
      margin: 4px 0 0;
      font-size: 11px;
      color: ${greyMui._700};
    }

    .log__item--translated-error {
      margin: 4px 0 0;
      font-size: 11px;
      color: ${orangeMui._900};
    }

    .log__item--message .log-highlight {
      background-color: ${rydeColors.yellow};
      border-radius: 2px;
      padding: 0 2px;
      font-weight: 500;
    }

    .log__id-strips {
      display: flex;
      flex-direction: column;
      gap: 6px;
      margin-top: 8px;
    }

    .log__id-strip {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px 8px;
      padding: 6px 10px;
      border-radius: 10px;
    }

    .log__id-strip--remaining {
      background: ${greenMui._50};
      border: 1px solid ${greenMui._100};
    }

    .log__id-strip--filtered-out {
      background: ${redMui._50};
      border: 1px solid ${redMui._100};
    }

    .log__id-strip-label {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.01em;
      color: ${greyMui._700};
    }

    .log__id-strip-ids {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }

    .log__id-chip {
      display: inline-flex;
      align-items: center;
      padding: 1px 7px;
      border: none;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.72);
      color: ${lightBlueMui._700};
      font-size: 12px;
      line-height: 1.4;
      text-decoration: none;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }

      &.log-highlight {
        background-color: ${rydeColors.yellow};
        font-weight: 600;
      }
    }

    .log__id-strip-empty {
      font-size: 12px;
      color: ${greyMui._500};
      font-style: italic;
    }

    .log__id-strip-more {
      border: 0;
      background: rgba(255, 255, 255, 0.85);
      color: ${greyMui._700};
      border-radius: 999px;
      padding: 1px 8px;
      font-size: 11px;
      font-weight: 600;
      cursor: pointer;
    }

    .log__item--error-details {
      margin-top: 8px;
      padding: 8px;
      background-color: ${redMui._50};
      border: 1px solid ${redMui._200};
      border-radius: 4px;
      border-inline-start: 3px solid ${redMui._500};
    }

    .log__item--error-details-content {
      font-size: 11px;
      color: ${redMui._800};
      line-height: 1.3;
      font-family: "Courier New", monospace;
      word-break: break-word;

      .log-highlight {
        background-color: ${rydeColors.yellow};
        border-radius: 2px;
        padding: 0 2px;
        font-weight: 500;
      }
    }
  }

  .log__no-logs-found {
    color: ${greyMui._900};
    font-size: 18px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .log__filters-footer {
    display: flex;
    flex-direction: column;
    gap: 12px;
    font-size: 14px;
    color: ${greyMui._600};
  }

  .log__filters-footer-time-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-bottom: 8px;
    border-bottom: 1px solid ${greyMui._200};
  }

  .log__filters-footer-time-info--failed {
    background-color: ${redMui._50};
    border-bottom: 1px solid ${redMui._200};
    border-radius: 6px;
    padding: 8px 12px;
    margin-bottom: 4px;

    .log__filters-footer-time-item {
      color: ${redMui._800};
    }
  }

  .log__filters-footer-time-info--in-progress {
    background-color: ${greenMui._50};
    border-bottom: 1px solid ${greenMui._200};
    border-radius: 6px;
    padding: 8px 12px;
    margin-bottom: 4px;

    .log__filters-footer-time-item {
      color: ${greenMui._800};
    }
  }

  .log__filters-footer-time-item {
    font-size: 12px;
    color: ${greyMui._700};
    font-weight: 500;
  }

  .log__filters-footer-duration {
    color: ${blueMui._700};
    font-weight: 600;
  }

  .log__filters-footer-main-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .log__filters-footer-separator {
    color: ${greyMui._600};
    margin-inline-start: 4px;
    display: inline-flex;
    align-items: center;
  }

  .log__filters-footer-stats {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .log__filters-footer-stats-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .log__filters-footer-main-count {
    font-size: 14px;
    color: ${greyMui._600};
    display: flex;
    align-items: center;
    min-height: 24px;
  }

  .log__filters-footer-total-count {
    padding-inline-start: 4px;
  }

  .log__filters-footer-page-info {
    font-size: 14px;
    color: ${greyMui._600};
    font-weight: 500;
    text-align: end;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .log__filters-footer-production-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
    padding: 8px 12px;
    background-color: ${greyMui._50};
    border-radius: 8px;
    border: 1px solid ${greyMui._200};
  }

  .log__filters-footer-stat-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    line-height: 1.2;
    flex: 0 0 calc(50% - 4px);
    min-width: 180px;
    gap: 4px;
    padding-inline-end: 8px;
  }

  .log__filters-footer-stat-label {
    color: ${greyMui._700};
    font-weight: 500;
  }

  .log__filters-footer-stat-value {
    color: ${blueMui._700};
    font-weight: 600;
  }

  .log__filters-footer-stat-label-error {
    color: ${redMui._700};
  }

  .log__filters-footer-stat-value-error {
    color: ${redMui._700};
  }

  .log__filters-footer-stat-label-booking {
    color: ${purpleMui._700};
  }

  .log__filters-footer-stat-value-booking {
    color: ${purpleMui._800};
  }

  .log__filters-footer-stat-label-ride {
    color: ${blueMui._700};
  }

  .log__filters-footer-stat-value-ride {
    color: ${blueMui._800};
  }

  .log__filters-footer-production-dates {
    margin-top: 4px;
    padding: 4px 12px;
    background-color: ${blueMui._50};
    border-radius: 6px;
    border: 1px solid ${blueMui._200};
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    align-items: center;
  }

  .log__filters-footer-production-date {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    line-height: 1.2;
  }

  .log__filters-footer-production-date-label {
    color: ${blueMui._700};
    font-weight: 500;
    white-space: nowrap;
  }

  .log__filters-footer-production-date-value {
    color: ${blueMui._800};
    font-weight: 600;
  }

  .log__filters-level-label {
    font-size: 14px;
    font-weight: 500;
  }

  .log__filters-level-label.color_critical {
    color: ${redMui._900};
  }

  .log__filters-level-label.color_error {
    color: ${redMui._800};
  }

  .log__filters-level-label.color_warn {
    color: ${orangeMui._900};
  }

  .log__filters-level-label.color_info {
    color: ${blueMui._700};
  }

  .log__filters-level-label.color_debug {
    color: ${greyMui._800};
  }

  .log__filters-level-label.color_details {
    color: ${purpleMui._700};
  }

  .log__filters-footer-text {
    font-size: 14px;
    color: ${greyMui._600};
  }

  .log__filters-footer .reset-link {
    font-size: 14px;
    color: ${greyMui._600};
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    font-family: inherit;
    text-decoration: none;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      text-decoration: underline;
      color: ${greyMui._800};
    }

    &:focus {
      outline: none;
      text-decoration: underline;
    }
  }

  .log__filters-footer-refresh-btn {
    background: none;
    border: none;
    cursor: pointer;
    margin-inline-start: 8px;
    padding: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${greyMui._600};
    border-radius: 4px;
    transition: all 0.2s ease;
    height: 24px;
    width: 24px;

    &:hover {
      background-color: ${greyMui._100};
      color: ${blueMui._700};
      transform: scale(1.1);
    }

    &:focus {
      outline: none;
      background-color: ${greyMui._100};
      color: ${blueMui._700};
    }

    &:active {
      transform: scale(0.95);
    }
  }

  .log__filters-level-checkboxes {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .log__filters-level-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  .log__filters-level-label {
    font-size: 13px;
    font-weight: 500;
    margin-inline-end: 8px;
    white-space: nowrap;
  }

  .log__filters-level-checkboxes-container {
    display: flex;
    flex-direction: row;
    gap: 6px;
    flex-wrap: wrap;
  }

  .log__filters-level-checkbox-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 6px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    border-radius: 4px;
    border: none;
    background: none;
    color: inherit;
    text-align: start;
    font: inherit;

    &:hover {
      background-color: ${greyMui._100};
    }

    &:focus-visible {
      outline: 2px solid ${blueMui._400};
      outline-offset: 1px;
    }
  }

  .log__filters-level-checkbox-control {
    display: inline-flex;
    align-items: center;
  }

  .log__filters-level-loader {
    width: 100%;
    height: 6px;
    border-radius: 999px;
    background-color: ${blueMui._100};
    box-shadow: inset 0 0 0 1px ${blueMui._200};
  }

  .log__filters-level-loader.loading {
    background: linear-gradient(
      90deg,
      ${blueMui._100},
      ${blueMui._300},
      ${blueMui._100}
    );
    background-size: 300% 100%;
    animation: ${subtleLoaderShimmer} 3s ease-in-out infinite;
  }

  .log__filters-sort-dropdown {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .log__filters-sort-row {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .log__filters-sort-label {
    font-size: 12px;
    font-weight: 500;
    color: ${greyMui._700};
    white-space: nowrap;
    min-width: 30px;
  }

  .log__filters-sort-container {
    position: relative;
    flex: 1;
    min-width: 0;
  }

  .log__filters-sort-button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 32px;
    padding: 0 8px;
    background-color: white;
    border: 1px solid ${greyMui._300};
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 13px;
    color: ${greyMui._800};

    &:hover {
      border-color: ${blueMui._400};
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    &:focus {
      outline: none;
      border-color: ${blueMui._500};
      box-shadow: 0 0 0 2px ${blueMui._100};
    }
  }

  .log__filters-sort-content {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;
  }

  .log__filters-sort-value {
    flex: 1;
    min-width: 0;
    text-align: start;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .log__filters-sort-content svg {
    width: 16px;
    height: 16px;
    color: ${greyMui._600};
  }

  .log__filters-sort-arrow {
    font-size: 10px;
    color: ${greyMui._500};
    transition: transform 0.2s ease;
    margin-inline-start: 4px;
    flex-shrink: 0;

    &.open {
      transform: rotate(180deg);
    }
  }

  .log__filters-sort-dropdown-menu {
    position: absolute;
    top: 100%;
    inset-inline-start: 0;
    inset-inline-end: 0;
    z-index: ${ScreenDepth.DROPDOWN_MENU};
    background-color: white;
    border: 1px solid ${greyMui._300};
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    margin-top: 2px;
    max-height: 200px;
    overflow-y: auto;
  }

  .log__filters-sort-option {
    display: block;
    width: 100%;
    padding: 8px 12px;
    background: none;
    border: none;
    text-align: start;
    font-size: 13px;
    color: ${greyMui._800};
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: ${greyMui._50};
    }

    &.selected {
      background-color: ${blueMui._50};
      color: ${blueMui._700};
      font-weight: 500;
    }

    &:first-child {
      border-radius: 6px 6px 0 0;
    }

    &:last-child {
      border-radius: 0 0 6px 6px;
    }
  }

  .log__filters-sort-option-content {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .log__filters-sort-option-content svg {
    width: 16px;
    height: 16px;
    color: inherit;
  }

  .log__filters--reset-actions .log__filters-reset-btn {
    margin-top: 0;
    align-self: center;
  }

  .log__filters-reset-btn {
    font-size: 12px;
    color: ${greyMui._600};
    background: none;
    border: 1px solid ${greyMui._300};
    padding: 6px 12px;
    margin: 0;
    font-family: inherit;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 6px;
    align-self: flex-end;
    margin-top: -4px;

    &:hover {
      background-color: ${greyMui._100};
      border-color: ${greyMui._400};
      color: ${greyMui._800};
    }

    &:focus {
      outline: none;
      background-color: ${greyMui._100};
      border-color: ${blueMui._400};
      color: ${greyMui._800};
    }
  }

  .log__panel-collapse-btn {
    position: absolute;
    top: 8px;
    inset-inline-end: 8px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${greyMui._600};
    border-radius: 4px;
    transition: all 0.2s ease;
    height: 24px;
    width: 24px;
    z-index: 10;

    &:hover {
      background-color: ${greyMui._100};
      color: ${blueMui._700};
    }

    &:focus {
      outline: none;
      background-color: ${greyMui._100};
      color: ${blueMui._700};
    }

    svg {
      transition: transform 0.2s ease;
    }

    &.collapsed svg {
      transform: rotate(-90deg);
    }
  }

  .log__panel-content {
    transition: all 0.3s ease;
    overflow: visible;

    &.collapsed {
      max-height: 0;
      padding-top: 0;
      padding-bottom: 0;
      margin-top: 0;
      margin-bottom: 0;
      opacity: 0;
      overflow: hidden;
    }
  }

  .log__filters-panel-title {
    font-size: 12px;
    font-weight: 400;
    color: ${greyMui._500};
    padding-inline-end: 28px;
    line-height: 20px;
  }

  .log__filters-panel:not(.collapsed) .log__filters-panel-title {
    margin-bottom: 8px;
    display: block;
  }

  .log__filters-panel.collapsed:not(.log__collapsible-filter-section),
  .log__stats-panel.collapsed {
    min-height: 32px;
    padding: 4px 12px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  .log-item__old {
    padding: 10px;
    margin-bottom: 10px;
    border-radius: 5px;
    border-inline-start: 5px solid ${blueMui._500};
    color: ${greyMui._900};
    background-color: #f4f4f9;
    word-wrap: break-word;
    word-break: break-word;
    ${({ $isRtl = true }) => {
      return {
        textAlign: $isRtl ? "end" : "start",
      };
    }};
  }

  .log__loading {
    width: 100%;
    height: 3px;
    border-radius: 2px;
    background: linear-gradient(
      90deg,
      ${greyMui._100},
      ${greyMui._200},
      ${greyMui._100}
    );
    background-size: 200% 100%;
    animation: ${subtleLoaderShimmer} 1.6s ease-in-out infinite;
    opacity: 0.9;
    margin-top: 8px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 12px;
  }
`;

export const RichLogsPaginationContainerStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "rich-logs-pagination-container",
  },
)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 16px 0;
  padding: 8px 0;
`;
